// Copyright (C) 2025, Lifefinder Systems International AB, all rights reserved.
import React, { useState, useMemo, useEffect, useRef } from 'react';

import { useAuth } from '../../../context/AuthContext';
import { useDevices } from '../../../context/DeviceContext';
import { buttonStyle } from '../../../styles/constants';
import type {
  MaintenanceProps,
  Warning,
  WarningsByDevice,
  LfDevice,
  Beacon,
  CheckinStation,
  LfGateway,
  DeviceCategoryTab,
  DeviceSubview,
  InfrastructureType,
  WarningFilterType,
  WarningType,
} from '../../../types';
import {
  warningAPI,
  beaconAPI,
  checkinAPI,
  gatewayAPI,
  lfDeviceAPI,
} from '../../../utils/apiClient';
import logger from '../../../utils/logger';
import { WARNING_TYPE_LABELS } from '../../../utils/maintenanceUtils';
import { Button } from '../../common/Button';
import { OperatingGuide } from '../../device/operatingGuide/OperatingGuide';
import { LoraConfig } from '../LoraConfig';

import { BeaconView } from './BeaconView';
import { CheckinHistory } from './CheckinHistory';
import { CheckinSummaryPicker } from './CheckinSummaryPicker';
import { CheckinView } from './CheckinView';
import { DefaultCenterSettings } from './DefaultCenterSettings';
import { DeviceCsvImport } from './DeviceCsvImport';
import { DeviceManagement } from './DeviceManagement';
import { DeviceView } from './DeviceView';
import { GatewayView } from './GatewayView';
import { GeofenceSymbolsModal } from './GeofenceSymbolsModal';
import { InfrastructureThresholdModal } from './InfrastructureThresholdModal';
import { OperatingGuideSettings } from './OperatingGuideSettings';
import { PositionFilterSettings } from './PositionFilterSettings';
import RelayStatusPanel from './RelayStatusPanel';
import { SolverSettings } from './SolverSettings';
import { ThresholdSettingsModal } from './ThresholdSettingsModal';
import { UserManagement } from './UserManagement';

/**
 * Maintenance Component - Device maintenance and warnings management
 *
 * SuperAdmin-only page for monitoring and managing device maintenance needs:
 * - Battery warnings (low voltage)
 * - Signal warnings (device not reporting)
 * - Position warnings (no position updates)
 * - Manual maintenance flags
 * - Warning acknowledgment with timestamps
 */
export const Maintenance: React.FC<MaintenanceProps> = ({ onClose, onOpenAreaEditor }) => {
  const {
    devices,
    showActiveIndicator,
    setShowActiveIndicator,
    showBatchTrackingButtons,
    setShowBatchTrackingButtons,
    showTraceButton,
    setShowTraceButton,
    geofenceFitboundsPadding,
    setGeofenceFitboundsPadding,
    geofencingEnabled,
    setGeofencingEnabled,
    showCheckinSummary,
    setShowCheckinSummary,
    showDeviceStatusBoxes,
    setShowDeviceStatusBoxes,
    box2MqttEnabled,
    setBox2MqttEnabled,
    webPushEnabled,
    setWebPushEnabled,
    emailNotificationsEnabled,
    setEmailNotificationsEnabled,
    smtpConfigured,
    emailAlarmTemplate,
    setEmailAlarmTemplate,
    relayDnilDeveui,
    relayDnilChannels,
    relayDnilSilenceMs,
    setRelayDnilConfig,
  } = useDevices();
  const { isSuperAdmin, isAdmin, isChecker, projection } = useAuth();
  const [deviceCategory, setDeviceCategory] = useState<DeviceCategoryTab>('devices');
  const [deviceSubview, setDeviceSubview] = useState<DeviceSubview>('warnings');
  const [infrastructureType, setInfrastructureType] = useState<InfrastructureType>('beacon');
  const [checkinSubview, setCheckinSubview] = useState<'stations' | 'history' | 'summary'>(
    'stations'
  );
  const [filter, setFilter] = useState<WarningFilterType>('all');
  const [beacons, setBeacons] = useState<Beacon[]>([]);
  const [checkins, setCheckins] = useState<CheckinStation[]>([]);
  const [lfDevices, setLfDevices] = useState<LfDevice[]>([]);
  const [gateways, setGateways] = useState<LfGateway[]>([]);
  const [gatewaysError, setGatewaysError] = useState<string | null>(null);
  const [selectedWarning, setSelectedWarning] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [acknowledgeNote, setAcknowledgeNote] = useState<string>('');
  const [warnings, setWarnings] = useState<Warning[]>([]);
  const [allWarnings, setAllWarnings] = useState<Warning[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactivityWarning, setShowInactivityWarning] = useState<boolean>(false);
  const [inactivityCountdown, setInactivityCountdown] = useState<number>(30);
  const [adminSubtab, setAdminSubtab] = useState<
    | 'devices'
    | 'users'
    | 'import'
    | 'lora'
    | 'infra-thresholds'
    | 'geofence'
    | 'warn-thresholds'
    | 'position-filter'
    | 'solver'
    | 'map-center'
    | 'operating-guide-config'
    | 'server-settings'
  >('devices');

  // Bumped when a relay-setting save is rejected so the uncontrolled inputs (keyed on the
  // saved value + this tick) remount back to the last saved value instead of keeping the
  // rejected text.
  const [relayFieldRevert, setRelayFieldRevert] = useState<number>(0);

  /** Save a relay setting; on rejection surface the server's message and revert the field. */
  const saveRelayConfig = async (
    config: Parameters<typeof setRelayDnilConfig>[0]
  ): Promise<void> => {
    try {
      await setRelayDnilConfig(config);
    } catch (err) {
      alert(
        `Failed to save relay setting: ${err instanceof Error ? err.message : 'unknown error'}`
      );
      setRelayFieldRevert((t) => t + 1);
    }
  };

  /**
   * Fetch all warnings for badge counts
   */
  const fetchAllWarnings = async (): Promise<void> => {
    try {
      const token = window.localStorage.getItem('token');
      if (!token) return;

      const data = await warningAPI.getWarnings({ status: 'active', device_status: 'active' });

      if (data.success && data.warnings) {
        setAllWarnings(data.warnings);
      }
    } catch (err) {
      console.error('Error fetching all warnings:', err);
    }
  };

  /**
   * Fetch warnings from backend API
   */
  const fetchWarnings = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const category =
        deviceCategory === 'devices' ? 'lf_device' : 'lf_beacon,lf_checkin,lf_gateway';
      const data = await warningAPI.getWarnings({
        status: 'active,acknowledged,auto_resolved',
        category,
        device_status: 'active',
      });

      if (data.success && data.warnings) {
        setWarnings(data.warnings);
      } else {
        throw new Error(data.error || 'Failed to fetch warnings');
      }
    } catch (err) {
      console.error('Error fetching warnings:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setWarnings([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch beacons from backend API
   */
  const fetchBeacons = async (): Promise<void> => {
    try {
      const data = await beaconAPI.getBeacons();

      if (data.success && data.beacons) {
        setBeacons(data.beacons);
      } else {
        throw new Error(data.error || 'Failed to fetch beacons');
      }
    } catch (err) {
      console.error('Error fetching beacons:', err);
      setBeacons([]);
    }
  };

  /**
   * Fetch check-in stations from backend API
   */
  const fetchCheckins = async (): Promise<void> => {
    try {
      const data = await checkinAPI.getCheckins();

      if (data.success && data.checkins) {
        setCheckins(data.checkins);
      } else {
        throw new Error(data.error || 'Failed to fetch check-in stations');
      }
    } catch (err) {
      console.error('Error fetching check-in stations:', err);
      setCheckins([]);
    }
  };

  /**
   * Fetch LF devices from backend API
   */
  const fetchLfDevices = async (): Promise<void> => {
    try {
      const data = await lfDeviceAPI.getLfDevices(true);

      if (data.success && data.devices) {
        setLfDevices(data.devices);
      } else {
        throw new Error(data.error || 'Failed to fetch devices');
      }
    } catch (err) {
      console.error('Error fetching devices:', err);
      setLfDevices([]);
    }
  };

  /**
   * Fetch LF gateways from backend API (with synced Chirpstack status)
   */
  const fetchGateways = async (): Promise<void> => {
    try {
      const data = await gatewayAPI.getGateways();

      if (data.success && data.gateways) {
        setGateways(data.gateways);
        setGatewaysError(null);
      } else {
        throw new Error(data.error || 'Failed to fetch gateways');
      }
    } catch (err) {
      console.error('Error fetching gateways:', err);
      setGatewaysError(err instanceof Error ? err.message : 'Unknown error');
      setGateways([]);
    }
  };

  /**
   * Toggle beacon on/off
   */
  const handleToggleBeacon = async (beaconId: string, newState: string): Promise<void> => {
    try {
      const data = await beaconAPI.toggle(beaconId, newState);
      if (data.success) {
        logger.debug(`Beacon ${beaconId} toggled to ${newState}`);
        await fetchBeacons();
      } else {
        alert(`Failed to change beacon status: ${data.error}`);
      }
    } catch (err) {
      console.error('Error toggling beacon:', err);
      alert(
        `Failed to change beacon status: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  /**
   * Toggle all beacons on a deck
   */
  const handleToggleDeck = async (deckNumber: number | string, newState: string): Promise<void> => {
    if (
      !window.confirm(
        `Do you want to ${newState === 'on' ? 'turn on' : 'turn off'} all beacons on Deck ${deckNumber}?`
      )
    ) {
      return;
    }

    try {
      const data = await beaconAPI.toggleDeck(deckNumber, newState);
      if (data.success) {
        logger.debug(`All beacons on deck ${deckNumber} toggled to ${newState}`);
        await fetchBeacons();
      } else {
        alert(`Failed to change beacon status: ${data.error}`);
      }
    } catch (err) {
      console.error('Error toggling deck:', err);
      alert(
        `Failed to change beacon status: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  /**
   * Toggle a single check-in station on/off
   */
  const handleToggleCheckin = async (checkinId: number, newState: string): Promise<void> => {
    try {
      const data = await checkinAPI.toggle(checkinId, newState);
      if (data.success) {
        logger.debug(`Check-in station ${checkinId} toggled to ${newState}`);
        await fetchCheckins();
      } else {
        alert(`Failed to change check-in station status: ${data.error}`);
      }
    } catch (err) {
      console.error('Error toggling check-in station:', err);
      alert(
        `Failed to change check-in station status: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  // Fetch all warnings for badge counts on mount and every 30 seconds
  useEffect(() => {
    fetchAllWarnings();
    const interval = setInterval(fetchAllWarnings, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch warnings on mount, when category changes, and every 30 seconds
  useEffect(() => {
    if (deviceCategory === 'devices' && deviceSubview === 'warnings') {
      fetchWarnings();
      const interval = setInterval(fetchWarnings, 30000);
      return () => clearInterval(interval);
    } else if (deviceCategory === 'devices' && deviceSubview === 'all-devices') {
      fetchLfDevices();
      const interval = setInterval(fetchLfDevices, 30000);
      return () => clearInterval(interval);
    } else if (deviceCategory === 'infrastructure' && infrastructureType === 'beacon') {
      fetchBeacons();
      const interval = setInterval(fetchBeacons, 30000);
      return () => clearInterval(interval);
    } else if (deviceCategory === 'infrastructure' && infrastructureType === 'gateway') {
      fetchGateways();
      const interval = setInterval(fetchGateways, 30000);
      return () => clearInterval(interval);
    } else if (deviceCategory === 'infrastructure' && infrastructureType === 'checkin') {
      fetchCheckins();
      const interval = setInterval(fetchCheckins, 30000);
      return () => clearInterval(interval);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceCategory, deviceSubview, infrastructureType]);

  // Ref for onClose to avoid closure issues
  const onCloseRef = useRef(onClose);

  // Update onClose ref when it changes
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  /**
   * Inactivity timeout - close maintenance view after 2 minutes of inactivity
   * Shows warning 30 seconds before closing
   */
  useEffect(() => {
    logger.debug('Inactivity timer initialized');
    let lastActivityTime = Date.now();
    let checkInterval: NodeJS.Timeout | null = null;

    const resetActivity = (): void => {
      const now = Date.now();
      // Throttle: only log once per 5 seconds to reduce console spam
      if (now - lastActivityTime > 5000) {
        logger.debug('Activity detected, resetting inactivity timer');
      }
      lastActivityTime = now;

      // Hide warning if it's showing
      if (showInactivityWarning) {
        setShowInactivityWarning(false);
        setInactivityCountdown(30);
      }
    };

    // Check inactivity every second
    checkInterval = setInterval(() => {
      const now = Date.now();
      const inactiveSeconds = Math.floor((now - lastActivityTime) / 1000);

      // Update countdown always (will only be visible when warning is shown)
      if (inactiveSeconds >= 90 && inactiveSeconds < 120) {
        const remaining = 120 - inactiveSeconds;
        setInactivityCountdown(remaining);

        // Show warning after 90 seconds
        setShowInactivityWarning(true);
      } else {
        setShowInactivityWarning(false);
      }

      // Close after 120 seconds
      if (inactiveSeconds >= 120) {
        logger.debug(`Closing due to ${inactiveSeconds}s of inactivity`);
        if (checkInterval) clearInterval(checkInterval);
        onCloseRef.current();
      }
    }, 1000);

    // Activity event listeners - including mousemove, scroll, click, keypress
    const activityEvents = ['click', 'scroll', 'mousemove', 'keypress', 'mousedown', 'touchstart'];
    activityEvents.forEach((event) => {
      window.addEventListener(event, resetActivity, { passive: true });
    });

    // Cleanup on unmount
    return () => {
      logger.debug('Cleaning up inactivity timers');
      if (checkInterval) clearInterval(checkInterval);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetActivity);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  /**
   * Handle "Stay" button click in inactivity warning
   */
  const handleStayActive = (): void => {
    // The click event itself will trigger resetActivity and reset the timer
    // Just hide the warning here
    setShowInactivityWarning(false);
    setInactivityCountdown(30);
  };

  /**
   * Group warnings by device for display
   */
  const warningsByDevice = useMemo<WarningsByDevice[]>(() => {
    const grouped: Record<string, Warning[]> = {};

    warnings.forEach((warning) => {
      if (!grouped[warning.device]) {
        grouped[warning.device] = [];
      }
      grouped[warning.device].push(warning);
    });

    return Object.entries(grouped).map(([deviceId, deviceWarnings]) => {
      // Find device info from devices context
      const device = devices.find((d) => d.text === deviceId || d.id === deviceId);

      return {
        deviceId,
        device,
        warnings: deviceWarnings,
        hasAcknowledged: deviceWarnings.some((w) => w.status === 'acknowledged'),
        hasAutoResolved: deviceWarnings.some((w) => w.status === 'auto_resolved'),
        hasCritical: deviceWarnings.some((w) => w.severity === 'critical' && w.status === 'active'),
      };
    });
  }, [warnings, devices]);

  /**
   * Filter warnings based on selected filter
   * - 'acknowledged' shows only devices with acknowledged warnings
   * - 'auto_resolved' shows only devices with auto-resolved warnings
   * - All other filters show only active warnings
   */
  const filteredWarnings = useMemo<WarningsByDevice[]>(() => {
    if (filter === 'acknowledged') {
      // Only show devices with acknowledged warnings
      return warningsByDevice.filter((w) => w.hasAcknowledged);
    }

    if (filter === 'auto_resolved') {
      // Only show devices with auto-resolved warnings
      return warningsByDevice.filter((w) => w.hasAutoResolved);
    }

    // For all other filters, only show devices with active warnings
    const activeDevices = warningsByDevice
      .map((device) => ({
        ...device,
        warnings: device.warnings.filter((w) => w.status === 'active'),
      }))
      .filter((device) => device.warnings.length > 0);

    if (filter === 'all' || filter === 'unacknowledged') {
      return activeDevices;
    }

    // Filter by warning type — each filter key may match multiple API warning types
    const typeMap: Record<string, WarningType[]> = {
      battery: ['battery_low'],
      signal: ['signal_weak'],
      noSignal: ['no_signal'],
      temperature: ['temperature_high', 'temperature_low'],
      humidity: ['humidity_high', 'humidity_low'],
    };
    const warningTypes = typeMap[filter];
    if (warningTypes) {
      return activeDevices.filter((device) =>
        device.warnings.some((warning) =>
          warningTypes.includes(warning.warning_type as WarningType)
        )
      );
    }

    return activeDevices;
  }, [warningsByDevice, filter]);

  /**
   * Handle warning acknowledgment
   */
  const handleAcknowledge = async (warningId: string): Promise<void> => {
    try {
      const data = await warningAPI.acknowledge(warningId, acknowledgeNote || null, 'acknowledged');

      if (data.success) {
        logger.debug('Warning acknowledged successfully:', warningId);
        // Refresh warnings to show updated status
        await fetchWarnings();
        setSelectedWarning(null);
        setAcknowledgeNote('');
      } else {
        throw new Error(data.error || 'Failed to acknowledge warning');
      }
    } catch (err) {
      console.error('Error acknowledging warning:', err);
      alert(
        `Failed to acknowledge warning: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  /**
   * Handle acknowledging all warnings for a device
   */
  const handleAcknowledgeDevice = async (deviceId: string): Promise<void> => {
    try {
      const data = await warningAPI.acknowledgeDevice(deviceId, acknowledgeNote || null);

      if (data.success) {
        logger.debug('All warnings acknowledged for device:', deviceId, data.acknowledged_count);
        // Refresh warnings to show updated status
        await fetchWarnings();
        setSelectedWarning(null);
        setSelectedDevice(null);
        setAcknowledgeNote('');
      } else {
        throw new Error(data.error || 'Failed to acknowledge device warnings');
      }
    } catch (err) {
      console.error('Error acknowledging device warnings:', err);
      alert(
        `Failed to acknowledge warnings: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  /**
   * Get warning count by category for badge display
   */
  const categoryWarningCounts = useMemo(() => {
    const deviceWarnings = allWarnings.filter((w) => w.device_category === 'lf_device');
    const infrastructureWarnings = allWarnings.filter(
      (w) =>
        w.device_category === 'lf_beacon' ||
        w.device_category === 'lf_checkin' ||
        w.device_category === 'lf_gateway'
    );

    return {
      devices: deviceWarnings.length,
      infrastructure: infrastructureWarnings.length,
      beacon: allWarnings.filter((w) => w.device_category === 'lf_beacon').length,
      checkin: allWarnings.filter((w) => w.device_category === 'lf_checkin').length,
      gateway: allWarnings.filter((w) => w.device_category === 'lf_gateway').length,
    };
  }, [allWarnings]);

  /**
   * Get warning count by type
   */
  const warningCounts = useMemo(() => {
    return {
      total: warningsByDevice.length,
      battery: warnings.filter((w) => w.warning_type === 'battery_low' && w.status === 'active')
        .length,
      signal: warnings.filter((w) => w.warning_type === 'signal_weak' && w.status === 'active')
        .length,
      noSignal: warnings.filter((w) => w.warning_type === 'no_signal' && w.status === 'active')
        .length,
      temperature: warnings.filter(
        (w) =>
          (w.warning_type === 'temperature_high' || w.warning_type === 'temperature_low') &&
          w.status === 'active'
      ).length,
      humidity: warnings.filter(
        (w) =>
          (w.warning_type === 'humidity_high' || w.warning_type === 'humidity_low') &&
          w.status === 'active'
      ).length,
      acknowledged: warnings.filter((w) => w.status === 'acknowledged').length,
      auto_resolved: warnings.filter((w) => w.status === 'auto_resolved').length,
      unacknowledged: warnings.filter((w) => w.status === 'active').length,
    };
  }, [warnings, warningsByDevice]);

  // Only allow Admin and above
  if (!isAdmin) {
    return (
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          padding: '2vmin',
          borderRadius: '1vmin',
          fontSize: '2vmin',
        }}
      >
        <h2>Access Denied</h2>
        <p>Only SuperAdmin has access to the maintenance page.</p>
        <Button text="Close" onClick={onClose} />
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: '#f5f5f5',
        // overflow: hidden here — the scroll is on the inner absolute div below.
        // iOS Safari does not scroll a position:fixed element that also has overflow:scroll;
        // separating the two (fixed outer + absolute inner) is the reliable workaround.
        overflow: 'hidden',
        zIndex: 2000,
      }}
    >
      {/* Scrollable inner container — must be absolute (not fixed) for iOS Safari to scroll it */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflowY: 'scroll',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Header */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            background: 'white',
            borderBottom: '2px solid #ddd',
            padding: '2vmin',
            zIndex: 2001,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1vmin',
            }}
          >
            <h1 style={{ fontSize: '3vmin', margin: 0 }}>🔧 Maintenance & Warnings</h1>
            <Button
              text="✕ Close"
              onClick={onClose}
              style={{
                background: '#f44',
                color: 'white',
                fontSize: '2vmin',
              }}
            />
          </div>

          {/* Category tabs */}
          <div
            style={{
              display: 'flex',
              gap: '1vmin',
              marginBottom: '1.5vmin',
              borderBottom: '2px solid #ddd',
              paddingBottom: '1vmin',
            }}
          >
            <Button
              text={
                <span style={{ display: 'flex', alignItems: 'center', gap: '1vmin' }}>
                  📱 Devices
                  {categoryWarningCounts.devices > 0 && (
                    <span
                      style={{
                        background: '#f44',
                        color: 'white',
                        borderRadius: '50%',
                        width: 'max(22px, 3vmin)',
                        height: 'max(22px, 3vmin)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 'max(10px, 1.5vmin)',
                        fontWeight: 'bold',
                      }}
                    >
                      {categoryWarningCounts.devices}
                    </span>
                  )}
                </span>
              }
              onClick={() => setDeviceCategory('devices')}
              style={{
                background: deviceCategory === 'devices' ? '#2196F3' : 'transparent',
                color: deviceCategory === 'devices' ? 'white' : '#666',
                fontSize: '2vmin',
                fontWeight: deviceCategory === 'devices' ? 'bold' : 'normal',
                border: 'none',
                borderBottom: deviceCategory === 'devices' ? '3px solid #2196F3' : 'none',
                borderRadius: 0,
                padding: '1vmin 2vmin',
              }}
            />
            <Button
              text={
                <span style={{ display: 'flex', alignItems: 'center', gap: '1vmin' }}>
                  🏗️ Infrastructure
                  {categoryWarningCounts.infrastructure > 0 && (
                    <span
                      style={{
                        background: '#f44',
                        color: 'white',
                        borderRadius: '50%',
                        width: 'max(22px, 3vmin)',
                        height: 'max(22px, 3vmin)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 'max(10px, 1.5vmin)',
                        fontWeight: 'bold',
                      }}
                    >
                      {categoryWarningCounts.infrastructure}
                    </span>
                  )}
                </span>
              }
              onClick={() => setDeviceCategory('infrastructure')}
              style={{
                background: deviceCategory === 'infrastructure' ? '#2196F3' : 'transparent',
                color: deviceCategory === 'infrastructure' ? 'white' : '#666',
                fontSize: '2vmin',
                fontWeight: deviceCategory === 'infrastructure' ? 'bold' : 'normal',
                border: 'none',
                borderBottom: deviceCategory === 'infrastructure' ? '3px solid #2196F3' : 'none',
                borderRadius: 0,
                padding: '1vmin 2vmin',
              }}
            />
            {isAdmin && (
              <Button
                text="⚙️ Admin"
                onClick={() => setDeviceCategory('admin')}
                style={{
                  background: deviceCategory === 'admin' ? '#2196F3' : 'transparent',
                  color: deviceCategory === 'admin' ? 'white' : '#666',
                  fontSize: '2vmin',
                  fontWeight: deviceCategory === 'admin' ? 'bold' : 'normal',
                  border: 'none',
                  borderBottom: deviceCategory === 'admin' ? '3px solid #2196F3' : 'none',
                  borderRadius: 0,
                  padding: '1vmin 2vmin',
                }}
              />
            )}
          </div>

          {/* Admin sub-tabs */}
          {deviceCategory === 'admin' && (
            <div
              style={{
                display: 'flex',
                gap: '0.5vmin',
                padding: '0.5vmin 2vmin 0',
                borderBottom: '1px solid #eee',
              }}
            >
              {(
                [
                  {
                    id: 'devices',
                    label: '📱 Devices',
                    superAdminOnly: false,
                    timespaceOnly: false,
                  },
                  { id: 'users', label: '👥 Users', superAdminOnly: false, timespaceOnly: false },
                  {
                    id: 'import',
                    label: '📥 CSV Import',
                    superAdminOnly: true,
                    timespaceOnly: false,
                  },
                  {
                    id: 'lora',
                    label: '📡 LoRa Config',
                    superAdminOnly: true,
                    timespaceOnly: false,
                  },
                  {
                    id: 'infra-thresholds',
                    label: '🏗️ Infrastructure Thresholds',
                    superAdminOnly: true,
                    timespaceOnly: false,
                  },
                  {
                    id: 'geofence',
                    label: '🗺️ Geofence Symbols',
                    superAdminOnly: true,
                    timespaceOnly: false,
                  },
                  {
                    id: 'warn-thresholds',
                    label: '⚙️ Device Thresholds',
                    superAdminOnly: true,
                    timespaceOnly: false,
                  },
                  {
                    id: 'position-filter',
                    label: '📍 Position Filter',
                    superAdminOnly: true,
                    timespaceOnly: false,
                  },
                  { id: 'solver', label: '🔀 Solver', superAdminOnly: true, timespaceOnly: false },
                  {
                    id: 'map-center',
                    label: '🗺️ Map Center',
                    superAdminOnly: true,
                    timespaceOnly: true,
                  },
                  {
                    id: 'operating-guide-config',
                    label: '📖 Operating Guide',
                    superAdminOnly: true,
                    timespaceOnly: false,
                  },
                  {
                    id: 'server-settings',
                    label: '⚙️ Server Settings',
                    superAdminOnly: true,
                    timespaceOnly: false,
                  },
                ] as const
              )
                .filter(
                  (sub) =>
                    (!sub.superAdminOnly || isSuperAdmin) &&
                    (!sub.timespaceOnly || projection === 'timespace')
                )
                .map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => setAdminSubtab(sub.id)}
                    style={{
                      background: adminSubtab === sub.id ? '#ede7f6' : 'transparent',
                      color: adminSubtab === sub.id ? '#7b2ff7' : '#555',
                      fontWeight: adminSubtab === sub.id ? 700 : 400,
                      border: 'none',
                      borderBottom:
                        adminSubtab === sub.id ? '2px solid #7b2ff7' : '2px solid transparent',
                      borderRadius: 0,
                      padding: '0.6vmin 1.4vmin',
                      cursor: 'pointer',
                      fontSize: 'max(12px, 1.6vmin)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sub.label}
                  </button>
                ))}
            </div>
          )}

          {/* Device sub-tabs */}
          {deviceCategory === 'devices' && (
            <div
              style={{
                display: 'flex',
                gap: '1vmin',
                marginBottom: '1.5vmin',
                paddingLeft: '2vmin',
              }}
            >
              <Button
                text="⚠️ Warnings"
                onClick={() => setDeviceSubview('warnings')}
                style={{
                  ...buttonStyle,
                  background: deviceSubview === 'warnings' ? '#4CAF50' : '#eee',
                  color: deviceSubview === 'warnings' ? 'white' : '#333',
                  fontSize: 'max(12px, 1.6vmin)',
                  padding: 'max(8px, 0.8vmin) max(12px, 1.5vmin)',
                  minWidth: 'auto',
                  minHeight: 'max(36px, 4vmin)',
                }}
              />
              <Button
                text="🔧 Device Maintenance"
                onClick={() => setDeviceSubview('all-devices')}
                style={{
                  ...buttonStyle,
                  background: deviceSubview === 'all-devices' ? '#4CAF50' : '#eee',
                  color: deviceSubview === 'all-devices' ? 'white' : '#333',
                  fontSize: 'max(12px, 1.6vmin)',
                  padding: 'max(8px, 0.8vmin) max(12px, 1.5vmin)',
                  minWidth: 'auto',
                  minHeight: 'max(36px, 4vmin)',
                }}
              />
              <Button
                text="📖 Operating Guide"
                onClick={() => setDeviceSubview('operating-guide')}
                style={{
                  ...buttonStyle,
                  background: deviceSubview === 'operating-guide' ? '#4CAF50' : '#eee',
                  color: deviceSubview === 'operating-guide' ? 'white' : '#333',
                  fontSize: 'max(12px, 1.6vmin)',
                  padding: 'max(8px, 0.8vmin) max(12px, 1.5vmin)',
                  minWidth: 'auto',
                  minHeight: 'max(36px, 4vmin)',
                }}
              />
            </div>
          )}

          {/* Infrastructure sub-tabs */}
          {deviceCategory === 'infrastructure' && (
            <>
              <div
                style={{
                  display: 'flex',
                  gap: '1vmin',
                  marginBottom: '1.5vmin',
                  paddingLeft: '2vmin',
                }}
              >
                <Button
                  text={
                    <>
                      📡 LF Beacon
                      {categoryWarningCounts.beacon > 0 && (
                        <span
                          style={{
                            marginLeft: '0.6vmin',
                            background: '#f44336',
                            color: 'white',
                            borderRadius: '999px',
                            padding: '1px max(4px, 0.5vmin)',
                            fontSize: 'max(9px, 1.1vmin)',
                            fontWeight: 'bold',
                            lineHeight: 'normal',
                            display: 'inline-block',
                            verticalAlign: 'middle',
                          }}
                        >
                          {categoryWarningCounts.beacon}
                        </span>
                      )}
                    </>
                  }
                  onClick={() => setInfrastructureType('beacon')}
                  style={{
                    ...buttonStyle,
                    background: infrastructureType === 'beacon' ? '#4CAF50' : '#eee',
                    color: infrastructureType === 'beacon' ? 'white' : '#333',
                    fontSize: 'max(12px, 1.6vmin)',
                    padding: 'max(8px, 0.8vmin) max(12px, 1.5vmin)',
                    minWidth: 'auto',
                    minHeight: 'max(36px, 4vmin)',
                  }}
                />
                <Button
                  text={
                    <>
                      🌐 LF Gateway
                      {categoryWarningCounts.gateway > 0 && (
                        <span
                          style={{
                            marginLeft: '0.6vmin',
                            background: '#f44336',
                            color: 'white',
                            borderRadius: '999px',
                            padding: '1px max(4px, 0.5vmin)',
                            fontSize: 'max(9px, 1.1vmin)',
                            fontWeight: 'bold',
                            lineHeight: 'normal',
                            display: 'inline-block',
                            verticalAlign: 'middle',
                          }}
                        >
                          {categoryWarningCounts.gateway}
                        </span>
                      )}
                    </>
                  }
                  onClick={() => setInfrastructureType('gateway')}
                  style={{
                    ...buttonStyle,
                    background: infrastructureType === 'gateway' ? '#4CAF50' : '#eee',
                    color: infrastructureType === 'gateway' ? 'white' : '#333',
                    fontSize: 'max(12px, 1.6vmin)',
                    padding: 'max(8px, 0.8vmin) max(12px, 1.5vmin)',
                    minWidth: 'auto',
                    minHeight: 'max(36px, 4vmin)',
                  }}
                />
                <Button
                  text={
                    <>
                      ✅ LF Check-in
                      {categoryWarningCounts.checkin > 0 && (
                        <span
                          style={{
                            marginLeft: '0.6vmin',
                            background: '#f44336',
                            color: 'white',
                            borderRadius: '999px',
                            padding: '1px max(4px, 0.5vmin)',
                            fontSize: 'max(9px, 1.1vmin)',
                            fontWeight: 'bold',
                            lineHeight: 'normal',
                            display: 'inline-block',
                            verticalAlign: 'middle',
                          }}
                        >
                          {categoryWarningCounts.checkin}
                        </span>
                      )}
                    </>
                  }
                  onClick={() => setInfrastructureType('checkin')}
                  style={{
                    ...buttonStyle,
                    background: infrastructureType === 'checkin' ? '#4CAF50' : '#eee',
                    color: infrastructureType === 'checkin' ? 'white' : '#333',
                    fontSize: 'max(12px, 1.6vmin)',
                    padding: 'max(8px, 0.8vmin) max(12px, 1.5vmin)',
                    minWidth: 'auto',
                    minHeight: 'max(36px, 4vmin)',
                  }}
                />
              </div>
            </>
          )}

          {/* Statistics - only show for devices category with warnings subview */}
          {deviceCategory === 'devices' && deviceSubview === 'warnings' && (
            <>
              <div
                style={{
                  display: 'flex',
                  gap: '2vmin',
                  flexWrap: 'wrap',
                  fontSize: 'max(13px, 1.8vmin)',
                  marginTop: '1vmin',
                }}
              >
                <div style={{ padding: '1vmin', background: '#ffebee', borderRadius: '0.5vmin' }}>
                  <strong>🪫 Battery:</strong> {warningCounts.battery}
                </div>
                <div style={{ padding: '1vmin', background: '#fff3e0', borderRadius: '0.5vmin' }}>
                  <strong>📡 Signal:</strong> {warningCounts.signal}
                </div>
                <div style={{ padding: '1vmin', background: '#e3f2fd', borderRadius: '0.5vmin' }}>
                  <strong>⚡ No signal:</strong> {warningCounts.noSignal}
                </div>
                <div style={{ padding: '1vmin', background: '#fce4ec', borderRadius: '0.5vmin' }}>
                  <strong>🌡️ Temperature:</strong> {warningCounts.temperature}
                </div>
                <div style={{ padding: '1vmin', background: '#e0f7fa', borderRadius: '0.5vmin' }}>
                  <strong>💧 Humidity:</strong> {warningCounts.humidity}
                </div>
                <div style={{ padding: '1vmin', background: '#e8f5e9', borderRadius: '0.5vmin' }}>
                  <strong>✓ Acknowledged:</strong> {warningCounts.acknowledged}
                </div>
                <div style={{ padding: '1vmin', background: '#e1f5fe', borderRadius: '0.5vmin' }}>
                  <strong>🔄 Auto-resolved:</strong> {warningCounts.auto_resolved}
                </div>
                <div style={{ padding: '1vmin', background: '#fce4ec', borderRadius: '0.5vmin' }}>
                  <strong>⚠ Active:</strong> {warningCounts.unacknowledged}
                </div>
              </div>

              {/* Filter buttons */}
              <div
                style={{
                  display: 'flex',
                  gap: '1vmin',
                  marginTop: '1vmin',
                  flexWrap: 'wrap',
                }}
              >
                {[
                  { key: 'all', label: 'All active' },
                  { key: 'unacknowledged', label: 'Not acknowledged' },
                  { key: 'battery', label: 'Battery' },
                  { key: 'signal', label: 'Signal' },
                  { key: 'noSignal', label: 'No signal' },
                  { key: 'temperature', label: 'Temperature' },
                  { key: 'humidity', label: 'Humidity' },
                  { key: 'acknowledged', label: 'Acknowledged' },
                  { key: 'auto_resolved', label: 'Auto-resolved' },
                ].map(({ key, label }) => (
                  <Button
                    key={key}
                    text={label}
                    onClick={() => setFilter(key as WarningFilterType)}
                    style={{
                      ...buttonStyle,
                      fontSize: 'max(12px, 1.6vmin)',
                      background: filter === key ? '#4CAF50' : '#eee',
                      color: filter === key ? 'white' : '#333',
                      minWidth: 'auto',
                      padding: 'max(8px, 0.8vmin) max(10px, 1.2vmin)',
                      minHeight: 'max(36px, 4vmin)',
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Content area */}
        <div
          style={{
            padding: '2vmin',
            paddingBottom: 'max(2vmin, env(safe-area-inset-bottom, 20px) + 20px)',
          }}
        >
          {/* Show OperatingGuide for devices > operating-guide */}
          {deviceCategory === 'devices' && deviceSubview === 'operating-guide' ? (
            <OperatingGuide />
          ) : deviceCategory === 'devices' && deviceSubview === 'all-devices' ? (
            <DeviceView devices={lfDevices} onRefresh={fetchLfDevices} />
          ) : deviceCategory === 'infrastructure' && infrastructureType === 'beacon' ? (
            <BeaconView
              beacons={beacons}
              onToggleBeacon={handleToggleBeacon}
              onToggleDeck={handleToggleDeck}
              onRefresh={fetchBeacons}
              isSuperAdmin={isSuperAdmin}
            />
          ) : deviceCategory === 'infrastructure' && infrastructureType === 'checkin' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vmin' }}>
              <div style={{ display: 'flex', gap: '1vmin' }}>
                <Button
                  text="Stations"
                  onClick={() => setCheckinSubview('stations')}
                  style={{
                    ...buttonStyle,
                    background: checkinSubview === 'stations' ? '#4CAF50' : '#eee',
                    color: checkinSubview === 'stations' ? 'white' : '#333',
                    fontSize: 'max(12px, 1.6vmin)',
                    padding: 'max(8px, 0.8vmin) max(12px, 1.5vmin)',
                    minWidth: 'auto',
                    minHeight: 'max(36px, 4vmin)',
                  }}
                />
                {(isChecker || isAdmin || isSuperAdmin) && (
                  <Button
                    text="History"
                    onClick={() => setCheckinSubview('history')}
                    style={{
                      ...buttonStyle,
                      background: checkinSubview === 'history' ? '#4CAF50' : '#eee',
                      color: checkinSubview === 'history' ? 'white' : '#333',
                      fontSize: 'max(12px, 1.6vmin)',
                      padding: 'max(8px, 0.8vmin) max(12px, 1.5vmin)',
                      minWidth: 'auto',
                      minHeight: 'max(36px, 4vmin)',
                    }}
                  />
                )}
                {isSuperAdmin && (
                  <Button
                    text="Summary"
                    onClick={() => setCheckinSubview('summary')}
                    style={{
                      ...buttonStyle,
                      background: checkinSubview === 'summary' ? '#4CAF50' : '#eee',
                      color: checkinSubview === 'summary' ? 'white' : '#333',
                      fontSize: 'max(12px, 1.6vmin)',
                      padding: 'max(8px, 0.8vmin) max(12px, 1.5vmin)',
                      minWidth: 'auto',
                      minHeight: 'max(36px, 4vmin)',
                    }}
                  />
                )}
              </div>
              {checkinSubview === 'stations' ? (
                <CheckinView
                  checkins={checkins}
                  onToggleCheckin={handleToggleCheckin}
                  onRefresh={fetchCheckins}
                  isSuperAdmin={isSuperAdmin}
                />
              ) : checkinSubview === 'history' ? (
                <CheckinHistory stations={checkins} />
              ) : (
                <CheckinSummaryPicker stations={checkins} />
              )}
            </div>
          ) : deviceCategory === 'infrastructure' && infrastructureType === 'gateway' ? (
            gatewaysError ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '4vmin',
                  fontSize: '2vmin',
                  color: '#f44',
                  background: 'white',
                  borderRadius: '1vmin',
                  border: '2px solid #f44',
                }}
              >
                <div style={{ marginBottom: '2vmin', fontSize: '4vmin' }}>⚠️</div>
                <div>
                  <strong>Gateway Error</strong>
                </div>
                <div style={{ fontSize: 'max(12px, 1.6vmin)', marginTop: '1vmin', color: '#666' }}>
                  {gatewaysError}
                </div>
                {gatewaysError.includes('token') && (
                  <div
                    style={{
                      fontSize: 'max(12px, 1.4vmin)',
                      marginTop: '1vmin',
                      color: '#888',
                      fontStyle: 'italic',
                    }}
                  >
                    Set CHIRPSTACK_API_TOKEN environment variable on the server
                  </div>
                )}
              </div>
            ) : (
              <GatewayView gateways={gateways} onRefresh={fetchGateways} />
            )
          ) : deviceCategory === 'admin' ? (
            <div style={{ padding: '1vmin' }}>
              {adminSubtab === 'devices' && <DeviceManagement />}
              {adminSubtab === 'users' && <UserManagement />}
              {adminSubtab === 'import' && <DeviceCsvImport />}
              {adminSubtab === 'lora' && (
                <LoraConfig inline onClose={() => setAdminSubtab('import')} />
              )}
              {adminSubtab === 'infra-thresholds' && (
                <InfrastructureThresholdModal
                  inline
                  onClose={() => setAdminSubtab('devices')}
                  onSave={() => {
                    setAdminSubtab('devices');
                    fetchWarnings();
                  }}
                />
              )}
              {adminSubtab === 'geofence' && (
                <GeofenceSymbolsModal inline onClose={() => setAdminSubtab('devices')} />
              )}
              {adminSubtab === 'warn-thresholds' && (
                <ThresholdSettingsModal
                  inline
                  onClose={() => setAdminSubtab('devices')}
                  onSave={() => setAdminSubtab('devices')}
                />
              )}
              {adminSubtab === 'position-filter' && (
                <PositionFilterSettings inline onClose={() => setAdminSubtab('devices')} />
              )}
              {adminSubtab === 'solver' && (
                <SolverSettings inline onClose={() => setAdminSubtab('devices')} />
              )}
              {adminSubtab === 'map-center' && (
                <DefaultCenterSettings inline onClose={() => setAdminSubtab('devices')} />
              )}
              {adminSubtab === 'operating-guide-config' && (
                <OperatingGuideSettings inline onClose={() => setAdminSubtab('devices')} />
              )}
              {adminSubtab === 'server-settings' &&
                (() => {
                  const ssCard: React.CSSProperties = {
                    background: '#faf9ff',
                    borderRadius: '0.5vmin',
                    borderLeft: '3px solid #7b2ff7',
                    padding: '1.2vmin 1.5vmin',
                    fontSize: 'max(12px, 1.5vmin)',
                  };
                  const ssCardTitle: React.CSSProperties = {
                    color: '#333',
                    fontWeight: 700,
                    fontSize: 'max(13px, 1.6vmin)',
                    marginBottom: '1vmin',
                  };
                  const ssRow: React.CSSProperties = {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8vmin',
                    marginBottom: '0.6vmin',
                    cursor: 'pointer',
                  };
                  const ssToggle = (active: boolean, disabled = false): React.CSSProperties => ({
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '3.6em',
                    padding: '0.15em 0.6em',
                    borderRadius: '12px',
                    border: 'none',
                    background: disabled ? '#eee' : active ? '#7b2ff7' : '#ddd',
                    color: disabled ? '#bbb' : active ? '#fff' : '#666',
                    fontSize: 'max(10px, 1.2vmin)',
                    fontWeight: 700,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    flexShrink: 0,
                    letterSpacing: '0.03em',
                  });
                  const ssLabel: React.CSSProperties = { color: '#444', userSelect: 'none' };
                  const ssDivider: React.CSSProperties = {
                    borderTop: '1px solid #e5e0f5',
                    margin: '0.8vmin 0',
                  };
                  const ssFormLabel: React.CSSProperties = {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.3vmin',
                    color: '#555',
                    fontSize: 'max(11px, 1.3vmin)',
                    fontWeight: 600,
                  };
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vmin' }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                          gap: '1.5vmin',
                        }}
                      >
                        {/* Map Display */}
                        <div style={ssCard}>
                          <div style={ssCardTitle}>🗺 Map Display</div>
                          <div
                            style={ssRow}
                            onClick={() => setShowActiveIndicator(!showActiveIndicator)}
                          >
                            <button
                              data-testid="show-active-on-map-toggle"
                              style={ssToggle(showActiveIndicator)}
                            >
                              {showActiveIndicator ? 'ON' : 'OFF'}
                            </button>
                            <span style={ssLabel}>Show active devices in green</span>
                          </div>
                          <div
                            style={ssRow}
                            onClick={() => setShowBatchTrackingButtons(!showBatchTrackingButtons)}
                          >
                            <button
                              data-testid="show-batch-tracking-buttons-toggle"
                              style={ssToggle(showBatchTrackingButtons)}
                            >
                              {showBatchTrackingButtons ? 'ON' : 'OFF'}
                            </button>
                            <span style={ssLabel}>Show Track All / Deactivate buttons</span>
                          </div>
                          <div style={ssRow} onClick={() => setShowTraceButton(!showTraceButton)}>
                            <button
                              data-testid="show-trace-button-toggle"
                              style={ssToggle(showTraceButton)}
                            >
                              {showTraceButton ? 'ON' : 'OFF'}
                            </button>
                            <span style={ssLabel}>Show Trace button in device popup</span>
                          </div>
                          <div
                            style={ssRow}
                            onClick={() => setShowCheckinSummary(!showCheckinSummary)}
                          >
                            <button
                              data-testid="show-checkin-summary-toggle"
                              style={ssToggle(showCheckinSummary)}
                            >
                              {showCheckinSummary ? 'ON' : 'OFF'}
                            </button>
                            <span style={ssLabel}>Show check-in station summary</span>
                          </div>
                          <div
                            style={ssRow}
                            onClick={() => setShowDeviceStatusBoxes(!showDeviceStatusBoxes)}
                          >
                            <button
                              data-testid="show-device-status-boxes-toggle"
                              style={ssToggle(showDeviceStatusBoxes)}
                            >
                              {showDeviceStatusBoxes ? 'ON' : 'OFF'}
                            </button>
                            <span style={ssLabel}>
                              Show active/alarming count boxes (timespace)
                            </span>
                          </div>
                          <div style={ssDivider} />
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5vmin',
                              color: '#555',
                            }}
                          >
                            <span>Geofence padding</span>
                            <input
                              type="number"
                              data-testid="geofence-fitbounds-padding-input"
                              value={geofenceFitboundsPadding}
                              min={10}
                              max={500}
                              step={10}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10);
                                if (Number.isInteger(v) && v >= 10 && v <= 500) {
                                  setGeofenceFitboundsPadding(v);
                                }
                              }}
                              style={{ width: '4.5em', marginLeft: '0.3vmin' }}
                            />
                            <span style={{ color: '#777' }}>px</span>
                          </label>
                        </div>

                        {/* Positioning */}
                        <div style={ssCard}>
                          <div style={ssCardTitle}>📍 Positioning</div>
                          <div
                            style={ssRow}
                            onClick={() => setGeofencingEnabled(!geofencingEnabled)}
                          >
                            <button
                              data-testid="geofencing-enabled-toggle"
                              style={ssToggle(geofencingEnabled)}
                            >
                              {geofencingEnabled ? 'ON' : 'OFF'}
                            </button>
                            <span style={ssLabel}>Enable geofencing (GEO_FENCING)</span>
                          </div>
                        </div>
                      </div>

                      {/* Alarm integrations */}
                      <div style={ssCard}>
                        <div style={ssCardTitle}>🔔 Alarm integrations</div>
                        <div style={ssRow} onClick={() => setBox2MqttEnabled(!box2MqttEnabled)}>
                          <button
                            data-testid="box2-mqtt-enabled-toggle"
                            style={ssToggle(box2MqttEnabled)}
                          >
                            {box2MqttEnabled ? 'ON' : 'OFF'}
                          </button>
                          <span style={ssLabel}>Publish alarm state to Box2 (MQTT)</span>
                        </div>
                        <div style={ssRow} onClick={() => setWebPushEnabled(!webPushEnabled)}>
                          <button
                            data-testid="web-push-enabled-toggle"
                            style={ssToggle(webPushEnabled)}
                          >
                            {webPushEnabled ? 'ON' : 'OFF'}
                          </button>
                          <span style={ssLabel}>Push notifications to browsers on alarm</span>
                        </div>
                        <div
                          style={ssRow}
                          onClick={() =>
                            smtpConfigured &&
                            setEmailNotificationsEnabled(!emailNotificationsEnabled)
                          }
                        >
                          <button
                            data-testid="email-notifications-enabled-toggle"
                            disabled={!smtpConfigured}
                            style={ssToggle(emailNotificationsEnabled, !smtpConfigured)}
                          >
                            {emailNotificationsEnabled ? 'ON' : 'OFF'}
                          </button>
                          <span style={ssLabel}>
                            Email notifications on alarm
                            <span
                              data-testid="smtp-status-indicator"
                              style={{
                                marginLeft: '0.5em',
                                fontSize: '0.85em',
                                fontWeight: 400,
                                color: smtpConfigured ? '#2e7d32' : '#b71c1c',
                              }}
                            >
                              {smtpConfigured ? '✓ SMTP configured' : '✗ SMTP not configured'}
                            </span>
                          </span>
                        </div>
                        {emailNotificationsEnabled && (
                          <div style={{ marginTop: '0.6vmin', marginLeft: '4.4em' }}>
                            <label
                              style={{ display: 'block', color: '#555', marginBottom: '0.3vmin' }}
                            >
                              Email alarm template
                              <span
                                style={{
                                  color: '#999',
                                  marginLeft: '0.4em',
                                  fontWeight: 400,
                                  fontSize: '0.9em',
                                }}
                              >
                                Placeholders: {'{{description}}'}, {'{{action}}'}
                              </span>
                            </label>
                            <textarea
                              data-testid="email-alarm-template"
                              defaultValue={emailAlarmTemplate}
                              key={emailAlarmTemplate}
                              rows={3}
                              style={{
                                width: '28em',
                                fontFamily: 'monospace',
                                fontSize: '0.9em',
                                resize: 'vertical',
                              }}
                              onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v && v !== emailAlarmTemplate) setEmailAlarmTemplate(v);
                              }}
                            />
                          </div>
                        )}
                        <div style={ssDivider} />
                        <div
                          style={{
                            color: '#555',
                            fontWeight: 600,
                            fontSize: 'max(11px, 1.35vmin)',
                            marginBottom: '0.6vmin',
                          }}
                        >
                          DNIL relay
                        </div>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'auto auto auto',
                            gap: '0.4vmin 1.5vmin',
                            alignItems: 'end',
                          }}
                        >
                          <label style={ssFormLabel}>
                            devEUI
                            <input
                              type="text"
                              data-testid="dnil-relay-deveui"
                              key={`${relayDnilDeveui}:${relayFieldRevert}`}
                              defaultValue={relayDnilDeveui}
                              placeholder="16 hex chars"
                              onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v !== relayDnilDeveui) saveRelayConfig({ relayDnilDeveui: v });
                              }}
                              style={{ width: '14em', fontFamily: 'monospace', fontWeight: 400 }}
                            />
                          </label>
                          <label style={ssFormLabel}>
                            Relay on alarm
                            <select
                              data-testid="dnil-relay-channels"
                              value={relayDnilChannels}
                              onChange={(e) =>
                                saveRelayConfig({ relayDnilChannels: Number(e.target.value) })
                              }
                              style={{ fontWeight: 400 }}
                            >
                              <option value={0}>Off</option>
                              <option value={1}>Channel 1</option>
                              <option value={2}>Channel 2</option>
                              <option value={3}>Both channels</option>
                            </select>
                          </label>
                          <label style={ssFormLabel}>
                            Offline after
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3vmin' }}>
                              <input
                                type="number"
                                data-testid="dnil-relay-silence-hours"
                                min={0.1}
                                max={720}
                                step={0.5}
                                key={`${relayDnilSilenceMs}:${relayFieldRevert}`}
                                defaultValue={
                                  Math.round((relayDnilSilenceMs / 3_600_000) * 10) / 10
                                }
                                onBlur={(e) => {
                                  const raw = Number(e.target.value);
                                  if (!Number.isFinite(raw) || raw <= 0) return;
                                  // Clamp to the input's advertised range (HTML min/max don't
                                  // constrain typed values); 0.1 h is above the server's 5-min floor.
                                  const hrs = Math.min(720, Math.max(0.1, raw));
                                  const ms = Math.round(hrs * 3_600_000);
                                  if (ms !== relayDnilSilenceMs) {
                                    saveRelayConfig({ relaySilenceThresholdMs: ms });
                                  }
                                }}
                                style={{ width: '5em', fontWeight: 400 }}
                              />
                              <span style={{ color: '#777', fontWeight: 400 }}>h</span>
                            </div>
                          </label>
                        </div>
                        <RelayStatusPanel
                          refreshKey={`${relayDnilDeveui}:${relayDnilChannels}:${relayDnilSilenceMs}`}
                        />
                      </div>

                      {/* Geofence areas */}
                      <div style={ssCard}>
                        <div style={ssCardTitle}>⬡ Geofence areas</div>
                        <button
                          data-testid="open-area-editor-button"
                          onClick={onOpenAreaEditor}
                          disabled={!onOpenAreaEditor}
                          style={{
                            padding: '0.5em 1.4em',
                            borderRadius: '4px',
                            border: 'none',
                            background: onOpenAreaEditor ? '#7b2ff7' : '#ccc',
                            color: '#fff',
                            cursor: onOpenAreaEditor ? 'pointer' : 'default',
                            fontSize: 'inherit',
                            fontWeight: 600,
                          }}
                        >
                          🗺 Manage areas
                        </button>
                      </div>
                    </div>
                  );
                })()}
            </div>
          ) : (
            /* Warning list for devices */
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(400px, 100%), 1fr))',
                gap: '2vmin',
              }}
            >
              {loading ? (
                <div
                  style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    padding: '4vmin',
                    fontSize: '2vmin',
                    color: '#666',
                  }}
                >
                  Loading warnings...
                </div>
              ) : error ? (
                <div
                  style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    padding: '4vmin',
                    fontSize: '2vmin',
                    color: '#f44',
                  }}
                >
                  Error fetching warnings: {error}
                </div>
              ) : filteredWarnings.length === 0 ? (
                <div
                  style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    padding: '4vmin',
                    fontSize: '2vmin',
                    color: '#666',
                  }}
                >
                  {filter === 'all' ? '✓ No active warnings' : `No warnings for filter: ${filter}`}
                </div>
              ) : (
                filteredWarnings.map((item) => {
                  const {
                    deviceId,
                    device,
                    warnings: deviceWarnings,
                    hasAcknowledged,
                    hasAutoResolved,
                    hasCritical,
                  } = item;
                  const activeWarningsCount = deviceWarnings.filter(
                    (w) => w.status === 'active'
                  ).length;

                  return (
                    <div
                      key={deviceId}
                      style={{
                        background: 'white',
                        borderRadius: '1vmin',
                        padding: '1.5vmin',
                        border: `2px solid ${hasCritical ? '#f44' : hasAcknowledged ? '#4CAF50' : hasAutoResolved ? '#03A9F4' : '#ff9800'}`,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    >
                      {/* Device header */}
                      <div
                        style={{
                          fontSize: '2vmin',
                          fontWeight: 'bold',
                          marginBottom: '1vmin',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '1vmin',
                        }}
                      >
                        <span style={{ flex: 1 }}>{device?.title || device?.text || deviceId}</span>
                        {activeWarningsCount > 1 && selectedDevice !== deviceId && (
                          <Button
                            text={`Acknowledge all (${activeWarningsCount})`}
                            onClick={() => setSelectedDevice(deviceId)}
                            style={{
                              background: '#2196F3',
                              color: 'white',
                              fontSize: 'max(12px, 1.4vmin)',
                              padding: '0.5vmin 1vmin',
                              whiteSpace: 'nowrap',
                            }}
                          />
                        )}
                        {hasAcknowledged && (
                          <span style={{ fontSize: 'max(12px, 1.5vmin)', color: '#4CAF50' }}>
                            ✓
                          </span>
                        )}
                      </div>

                      {/* Device ID */}
                      <div
                        style={{
                          fontSize: 'max(12px, 1.4vmin)',
                          color: '#666',
                          marginBottom: '1vmin',
                        }}
                      >
                        ID: {deviceId}
                      </div>

                      {/* Acknowledge all form */}
                      {selectedDevice === deviceId && (
                        <div
                          style={{
                            marginBottom: '1vmin',
                            padding: '1vmin',
                            background: '#f0f8ff',
                            borderRadius: '0.5vmin',
                            border: '1px solid #2196F3',
                          }}
                        >
                          <div
                            style={{
                              fontSize: 'max(12px, 1.6vmin)',
                              fontWeight: 'bold',
                              marginBottom: '0.5vmin',
                            }}
                          >
                            Acknowledge all {activeWarningsCount} warnings
                          </div>
                          <textarea
                            value={acknowledgeNote}
                            onChange={(e) => setAcknowledgeNote(e.target.value)}
                            placeholder="Note (optional)..."
                            style={{
                              width: '100%',
                              minHeight: '50px',
                              fontSize: 'max(12px, 1.4vmin)',
                              padding: '0.5vmin',
                              border: '1px solid #ccc',
                              borderRadius: '0.3vmin',
                              marginBottom: '0.5vmin',
                              boxSizing: 'border-box',
                            }}
                          />
                          <div style={{ display: 'flex', gap: '0.5vmin' }}>
                            <Button
                              text="✓ Acknowledge all"
                              onClick={() => handleAcknowledgeDevice(deviceId)}
                              style={{
                                background: '#4CAF50',
                                color: 'white',
                                fontSize: 'max(12px, 1.4vmin)',
                                flex: 1,
                              }}
                            />
                            <Button
                              text="Cancel"
                              onClick={() => {
                                setSelectedDevice(null);
                                setAcknowledgeNote('');
                              }}
                              style={{
                                background: '#ccc',
                                color: '#333',
                                fontSize: 'max(12px, 1.4vmin)',
                                flex: 1,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Warnings */}
                      <div style={{ marginBottom: '1vmin' }}>
                        {deviceWarnings.map((warning) => {
                          const isSelected = selectedWarning === warning.id;
                          const isAcknowledged = warning.status === 'acknowledged';
                          const isAutoResolved = warning.status === 'auto_resolved';
                          const acks = warning.acknowledgements || [];

                          return (
                            <div
                              key={warning.id}
                              style={{
                                fontSize: 'max(12px, 1.6vmin)',
                                padding: '1vmin',
                                marginBottom: '1vmin',
                                background: isAutoResolved
                                  ? '#e1f5fe'
                                  : warning.severity === 'critical'
                                    ? '#ffebee'
                                    : '#fff3e0',
                                borderLeft: `3px solid ${isAutoResolved ? '#03A9F4' : warning.severity === 'critical' ? '#f44' : '#ff9800'}`,
                                borderRadius: '0.3vmin',
                              }}
                            >
                              <div style={{ marginBottom: '0.5vmin', fontWeight: 'bold' }}>
                                {WARNING_TYPE_LABELS[warning.warning_type] || warning.warning_type}
                                {isAcknowledged && (
                                  <span style={{ marginLeft: '1vmin', color: '#4CAF50' }}>
                                    ✓ Acknowledged
                                  </span>
                                )}
                                {isAutoResolved && (
                                  <span style={{ marginLeft: '1vmin', color: '#03A9F4' }}>
                                    🔄 Auto-resolved
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 'max(12px, 1.4vmin)', color: '#666' }}>
                                Value:{' '}
                                {warning.current_value !== null
                                  ? Math.round(warning.current_value * 10) / 10
                                  : 'N/A'}
                                {warning.threshold_value !== null &&
                                  ` (threshold: ${warning.threshold_value})`}
                              </div>
                              <div
                                style={{
                                  fontSize: 'max(11px, 1.3vmin)',
                                  color: '#888',
                                  marginTop: '0.3vmin',
                                }}
                              >
                                Detected: {new Date(warning.detected_at).toLocaleString()}
                                {isAutoResolved && warning.resolved_at && (
                                  <div style={{ color: '#03A9F4', marginTop: '0.3vmin' }}>
                                    Auto-resolved: {new Date(warning.resolved_at).toLocaleString()}
                                    {warning.resolution_note && ` - ${warning.resolution_note}`}
                                  </div>
                                )}
                              </div>

                              {/* Acknowledgment info */}
                              {isAcknowledged && acks.length > 0 && (
                                <div
                                  style={{
                                    fontSize: 'max(11px, 1.3vmin)',
                                    color: '#4CAF50',
                                    fontStyle: 'italic',
                                    marginTop: '0.5vmin',
                                    padding: '0.5vmin',
                                    background: '#e8f5e9',
                                    borderRadius: '0.3vmin',
                                  }}
                                >
                                  <div>
                                    Acknowledged:{' '}
                                    {new Date(acks[0].acknowledged_at).toLocaleString()}
                                  </div>
                                  <div>By: {acks[0].acknowledged_by}</div>
                                  {acks[0].note && <div>Note: {acks[0].note}</div>}
                                </div>
                              )}

                              {/* Acknowledgment form */}
                              {!isAcknowledged && !isAutoResolved && (
                                <div style={{ marginTop: '0.5vmin' }}>
                                  {!isSelected ? (
                                    <Button
                                      text="Acknowledge"
                                      onClick={() => setSelectedWarning(warning.id)}
                                      style={{
                                        background: '#2196F3',
                                        color: 'white',
                                        fontSize: 'max(11px, 1.3vmin)',
                                        padding: '0.5vmin 1vmin',
                                      }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        marginTop: '0.5vmin',
                                        padding: '0.5vmin',
                                        background: '#f9f9f9',
                                        borderRadius: '0.3vmin',
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <textarea
                                        value={acknowledgeNote}
                                        onChange={(e) => setAcknowledgeNote(e.target.value)}
                                        placeholder="Note (optional)..."
                                        style={{
                                          width: '100%',
                                          minHeight: '40px',
                                          fontSize: 'max(11px, 1.3vmin)',
                                          padding: '0.5vmin',
                                          border: '1px solid #ccc',
                                          borderRadius: '0.3vmin',
                                          marginBottom: '0.5vmin',
                                          boxSizing: 'border-box',
                                        }}
                                      />
                                      <div style={{ display: 'flex', gap: '0.5vmin' }}>
                                        <Button
                                          text="✓ Acknowledge"
                                          onClick={() => handleAcknowledge(warning.id)}
                                          style={{
                                            background: '#4CAF50',
                                            color: 'white',
                                            fontSize: 'max(11px, 1.3vmin)',
                                            flex: 1,
                                          }}
                                        />
                                        <Button
                                          text="Cancel"
                                          onClick={() => {
                                            setSelectedWarning(null);
                                            setAcknowledgeNote('');
                                          }}
                                          style={{
                                            background: '#ccc',
                                            color: '#333',
                                            fontSize: 'max(11px, 1.3vmin)',
                                            flex: 1,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
      {/* end scrollable inner container */}

      {/* Inactivity Warning */}
      {showInactivityWarning && (
        <div
          style={{
            position: 'fixed',
            bottom: '2vmin',
            right: '2vmin',
            background: '#FF9800',
            color: 'white',
            padding: '2vmin',
            borderRadius: '1vmin',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 3000,
            minWidth: '300px',
            maxWidth: '400px',
            border: '2px solid #F57C00',
          }}
        >
          <div
            style={{
              fontSize: '2vmin',
              fontWeight: 'bold',
              marginBottom: '1vmin',
              display: 'flex',
              alignItems: 'center',
              gap: '1vmin',
            }}
          >
            ⏱️ Inactivity Warning
          </div>
          <div
            style={{
              fontSize: 'max(12px, 1.6vmin)',
              marginBottom: '1.5vmin',
              lineHeight: '1.5',
            }}
          >
            Closing in <strong style={{ fontSize: '2.2vmin' }}>{inactivityCountdown}</strong>{' '}
            seconds
          </div>
          <Button
            text="Stay Active"
            onClick={handleStayActive}
            style={{
              background: 'white',
              color: '#FF9800',
              fontSize: 'max(12px, 1.6vmin)',
              fontWeight: 'bold',
              padding: '1vmin 2vmin',
              width: '100%',
              border: 'none',
              borderRadius: '0.5vmin',
              cursor: 'pointer',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Maintenance;
